import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { styled, useTheme } from '@mui/material/styles';
import Typography, { TypographyProps } from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';

import { JobTalentNotes } from './JobTalentNotes';
import { useAuthStore } from '@bloomays-lib/common.auth/browser';
import { Role, TalentBoardEventType, TalentBoardPersistedEventType } from '@bloomays-lib/types.shared';
import { useTalentBoardEvents } from '../../hooks/useTalentBoardEvents';
import { TalentCV } from '@bloomays-lib/ui.shared';
import TalentProfile from './TalentProfile';
import TalentJobData from './TalentJobData';
import { useTalentBoardStore } from '../../stores/talentBoardStore';
import { Logger } from '../../services/service-logger';

const logger = Logger('TalentJob');

const getCvUrlForTalentBoard = (
  cv: string | null | undefined,
  jobId: string,
  talentId: string,
  talentsApiBase: string,
): string => {
  if (!cv) return '';
  if (!cv.includes('/talents/') || !jobId) return cv;
  const base = talentsApiBase.endsWith('/') ? talentsApiBase : `${talentsApiBase}/`;
  return `${base}files/talents/by-job/${jobId}/${talentId}/CV`;
};

type TabButtonProps = {
  isActive: boolean;
  onClick: () => void;
  text: string;
  iconName: 'profile' | 'notes' | 'cv';
};

const TabButton = ({ isActive, onClick, text, iconName }: TabButtonProps) => {
  let icon = null;
  switch (iconName) {
    case 'profile':
      icon = <PersonOutlineOutlinedIcon />;
      break;
    case 'notes':
      icon = <InsertDriveFileOutlinedIcon />;
      break;
    case 'cv':
      icon = <DescriptionOutlinedIcon />;
      break;
  }

  return (
    <ModalTabButton isActive={isActive} onClick={onClick}>
      {icon}
      <ModalButtonText>{text}</ModalButtonText>
    </ModalTabButton>
  );
};

export type TalentJobProps = {
  jobName: string;
  jobId: string;
};

const TalentJob = ({ jobName, jobId }: TalentJobProps) => {
  const theme = useTheme();
  const auth = useAuthStore((state) => state.auth);
  const isClient = auth?.user?.roles?.includes(Role.Client) || false;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation('talentBoard');
  const { modalTab, setModalTab, selectedTalent: talent, modalContentTab, setModalContentTab } = useTalentBoardStore();
  const { createTalentBoardEvent } = useTalentBoardEvents();

  useEffect(() => {
    if (!talent) {
      return;
    }

    logger.info('Traffic', {
      page: 'TalentBoard',
      data: { jobId, talentId: talent.id },
      evt: {
        name: TalentBoardEventType.TalentboardOpenTalent,
      },
      job: {
        id: jobId,
        name: jobName,
      },
      talent: {
        id: talent.id,
        name: talent.firstname + ' ' + talent.lastname,
      },
    });

    if (!isClient) {
      return;
    }

    void createTalentBoardEvent({
      jobId,
      date: new Date(),
      type: TalentBoardPersistedEventType.TalentboardOpenTalent,
      data: {
        talentId: talent.id,
        talentFullName: talent.firstname + ' ' + talent.lastname,
      },
    });
  }, [talent?.id, jobId]);

  if (!talent) return null;

  return (
    <Container>
      <ModalTabButtonWrapper>
        <TabButton
          isActive={modalTab === 'notes'}
          onClick={() => {
            logger.info('Traffic', {
              page: 'TalentJob',
              evt: { name: TalentBoardEventType.TalentboardOpenNotesTalent },
              job: {
                id: jobId,
                name: jobName,
              },
              talent: {
                id: talent.id,
                name: talent.firstname + ' ' + talent.lastname,
              },
            });
            setModalTab('notes');
          }}
          text={t('talentJob.notes')}
          iconName="notes"
        />
        <TabButton
          isActive={modalTab === 'profile'}
          onClick={() => {
            logger.info('Traffic', {
              page: 'TalentJob',
              evt: { name: TalentBoardEventType.TalentboardOpenProfileTalent },
              job: {
                id: jobId,
                name: jobName,
              },
              talent: {
                id: talent.id,
                name: talent.firstname + ' ' + talent.lastname,
              },
            });
            setModalTab('profile');
          }}
          text={t('talentJob.profile')}
          iconName="profile"
        />
        <TabButton
          isActive={modalTab === 'cv'}
          onClick={() => {
            logger.info('Traffic', {
              page: 'TalentJob',
              evt: { name: TalentBoardEventType.TalentboardOpenCvProfile },
              job: {
                id: jobId,
                name: jobName,
              },
              talent: {
                id: talent.id,
                name: talent.firstname + ' ' + talent.lastname,
              },
            });
            setModalTab('cv');
          }}
          text={t('talentJob.cv')}
          iconName="cv"
        />
      </ModalTabButtonWrapper>
      {((isMobile && modalTab === 'notes') || !isMobile) && (
        <SideProfile>
          <TalentJobData talent={talent} jobName={jobName} jobId={jobId} isClient={isClient} />
        </SideProfile>
      )}
      <Content>
        {modalTab === 'notes' && (
          <JobTalentNotes notes={talent.phaseNotes} generalEvaluation={talent.generalTamEvaluation} />
        )}
        {modalTab === 'profile' && (
          <TalentProfile
            jobId={jobId}
            jobName={jobName}
            talent={talent}
            contentTab={modalContentTab}
            setContentTab={setModalContentTab}
          />
        )}
        {modalTab === 'cv' && (
          <TalentCV
            cv={getCvUrlForTalentBoard(talent.cv, jobId, talent.id, import.meta.env.VITE_TALENTS_API as string)}
            emptyText={t('talentJob.emptyCV')}
            emptySx={{ marginTop: '160px', [theme.breakpoints.down('md')]: { marginTop: '60px' } }}
          />
        )}
      </Content>
    </Container>
  );
};

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  position: 'relative',
  height: '100%',
  backgroundColor: theme.palette.neutral.white,
  [theme.breakpoints.down('md')]: {
    backgroundColor: theme.palette.neutral[10],
    position: 'unset',
    flexDirection: 'column',
  },
}));

const SideProfile = styled('div')(({ theme }) => ({
  minWidth: '364px',
  maxWidth: '364px',
  marginTop: '23px',
  backgroundColor: theme.palette.neutral.white,
  overflowY: 'auto',
  [theme.breakpoints.down('md')]: {
    marginTop: '0px',
    minWidth: 'unset',
    maxWidth: 'unset',
    overflowY: 'unset',
  },
}));

const Content = styled('div')(({ theme }) => ({
  width: '100%',
  padding: '24px',
  backgroundColor: theme.palette.red[20],
  overflowY: 'auto',
  [theme.breakpoints.down('md')]: {
    overflowY: 'unset',
    padding: '0px',
    backgroundColor: theme.palette.neutral.white,
  },
}));

const ModalTabButtonWrapper = styled('div')(({ theme }) => ({
  position: 'absolute',
  left: '-70px',
  top: '48px',
  display: 'flex',
  width: '70px',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '16px',
  [theme.breakpoints.down('md')]: {
    position: 'relative',
    left: '0',
    top: '0',
    flexDirection: 'row',
    justifyContent: 'center',
    width: '100%',
    padding: '24px',
    gap: '8px',
  },
}));

const ModalButtonText: React.FC<TypographyProps> = styled(Typography)(() => ({
  fontSize: '14px',
}));

const ModalTabButton = styled('div')<{ isActive: boolean }>(({ isActive, theme }) => ({
  width: '100%',
  height: '70px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  paddingLeft: '15%',
  borderRadius: '99px 0 0 99px',
  fontSize: '14px',
  cursor: 'pointer',
  background: isActive ? theme.palette.neutral.white : theme.palette.gradient.red,
  '&:hover': {
    color: theme.palette.neutral.white,
    background: theme.palette.neutral.black,
  },
  [theme.breakpoints.down('md')]: {
    background: isActive ? theme.palette.gradient.red : 'transparent',
    height: 'unset',
    padding: '8px 16px',
    flexDirection: 'row',
    borderRadius: '20px',
    width: 'unset',
    ...(isActive && {
      '@media (max-width: 350px)': {
        '& > .MuiTypography-root': {
          display: 'none',
        },
      },
    }),
  },
}));

export default TalentJob;
